/* ============================================================================
   sfb-write-review.js — branded "Write a Review" modal behavior.
   Ported from designs/product sections/review-section.html (open/close,
   rating, counters), with the submit handler replaced by a fetch() to the
   Vercel /api/reviews proxy (which forwards to Judge.me).

   Open from anywhere: document.dispatchEvent(new CustomEvent('sfb:write-review:open'))
   ============================================================================ */
(function () {
  'use strict';

  var API_URL = 'https://sfb-vercel-api.vercel.app/api/reviews';
  var MIN_BODY = 50;

  function init() {
    var scrim = document.getElementById('sfb-wr-scrim');
    if (!scrim || scrim.dataset.sfbWrInit === '1') return;
    scrim.dataset.sfbWrInit = '1';

    var form        = scrim.querySelector('#sfb-wr-form');
    var body        = scrim.querySelector('[data-wr-body]');
    var foot        = scrim.querySelector('[data-wr-foot]');
    var done        = scrim.querySelector('[data-wr-done]');
    var errorEl     = scrim.querySelector('[data-wr-error]');
    var submitBtn   = scrim.querySelector('[data-wr-submit]');
    var submitLabel = scrim.querySelector('[data-wr-submit-label]');
    var lastFocus   = null;

    /* ── Thumbnail fallback ──
       A product image that errors (stale/missing media) or hangs (the
       `shopify theme dev` CDN proxy sometimes never resolves) would otherwise
       leave the browser's broken-image icon. Reveal the branded bottle
       placeholder, but yield back to the real image if it arrives late. */
    var thumbImg = scrim.querySelector('.sfb-wr__thumb-img');
    if (thumbImg) {
      var thumbWrap = thumbImg.closest('.sfb-wr__thumb');
      var thumbLoaded = function () { return thumbImg.complete && thumbImg.naturalWidth > 0; };
      var showPh = function () { if (thumbWrap) thumbWrap.classList.add('sfb-wr__thumb--ph'); };
      var hidePh = function () { if (thumbWrap) thumbWrap.classList.remove('sfb-wr__thumb--ph'); };
      if (!thumbLoaded()) {
        thumbImg.addEventListener('load', hidePh);
        thumbImg.addEventListener('error', function () { showPh(); if (thumbImg.parentNode) thumbImg.parentNode.removeChild(thumbImg); });
        setTimeout(function () { if (!thumbLoaded()) showPh(); }, 2500);
      }
    }

    /* ── Open / close ── */
    function open() {
      lastFocus = document.activeElement;
      scrim.hidden = false;
      void scrim.offsetWidth; // reflow so the transition runs from hidden
      scrim.classList.add('is-open');
      document.body.classList.add('sfb-wr-locked');
      var firstStar = scrim.querySelector('#sfb-wr-stars button');
      if (firstStar) firstStar.focus();
    }
    function close() {
      scrim.classList.remove('is-open');
      document.body.classList.remove('sfb-wr-locked');
      var onEnd = function () { scrim.hidden = true; scrim.removeEventListener('transitionend', onEnd); };
      scrim.addEventListener('transitionend', onEnd);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.addEventListener('sfb:write-review:open', open);
    scrim.addEventListener('click', function (e) {
      if (e.target === scrim) close();
      if (e.target.closest('[data-wr-close]')) { e.preventDefault(); close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && scrim.classList.contains('is-open')) close();
    });

    // Focus trap within the dialog while open
    scrim.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !scrim.classList.contains('is-open')) return;
      var f = scrim.querySelectorAll('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])');
      var list = Array.prototype.filter.call(f, function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* ── Rating ── */
    var starsEl   = scrim.querySelector('#sfb-wr-stars');
    var stars     = scrim.querySelectorAll('#sfb-wr-stars button');
    var rateLabel = scrim.querySelector('#sfb-wr-rate-label');
    var rateValue = scrim.querySelector('#sfb-wr-rating');

    // Cumulative fill: light every star up to and including the nth.
    function paint(n) {
      stars.forEach(function (b, i) { b.classList.toggle('lit', i < n); });
    }
    function renderLabel(n) {
      rateLabel.innerHTML = n
        ? n + '<small>out of 5</small>'
        : 'Tap a star<small>How was it?</small>';
    }
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Quick staggered "pop" across the committed stars for tactile feedback.
    function celebrate(v) {
      if (reduceMotion) return;
      stars.forEach(function (b) { b.classList.remove('pop'); });
      void starsEl.offsetWidth; // reflow so the animation restarts on re-click
      stars.forEach(function (b, i) {
        if (i < v) {
          b.style.setProperty('--pop-delay', (i * 45) + 'ms');
          b.classList.add('pop');
        }
      });
    }

    // Commit a rating (on click): updates value + a11y state, then repaints.
    function setRating(v) {
      stars.forEach(function (b, i) {
        b.setAttribute('aria-checked', i === v - 1 ? 'true' : 'false');
      });
      rateValue.value = v;
      paint(v);
      renderLabel(v);
      celebrate(v);
    }
    stars.forEach(function (b, i) {
      b.addEventListener('click', function () { setRating(+b.dataset.val); });
      // Hover preview (visual only — does not change the committed value).
      b.addEventListener('mouseenter', function () { paint(i + 1); renderLabel(i + 1); });
    });
    // Restore the committed selection when the pointer leaves the stars.
    if (starsEl) {
      starsEl.addEventListener('mouseleave', function () {
        var cur = +rateValue.value || 0;
        paint(cur);
        renderLabel(cur);
      });
    }

    /* ── Title quote-frame + counter ── */
    var titleInput = scrim.querySelector('#sfb-wr-title-input');
    var titleWrap  = scrim.querySelector('#sfb-wr-title-wrap');
    var titleN     = scrim.querySelector('#sfb-wr-title-n');
    function updateTitle() {
      var len = titleInput.value.length;
      titleN.textContent = len;
      titleWrap.classList.toggle('is-empty', len === 0);
    }
    titleInput.addEventListener('input', updateTitle);
    updateTitle();

    /* ── Body counter ── */
    var bodyInput = scrim.querySelector('#sfb-wr-body-input');
    var bodyN     = scrim.querySelector('#sfb-wr-body-n');
    bodyN.textContent = bodyInput.value.length;
    bodyInput.addEventListener('input', function () { bodyN.textContent = bodyInput.value.length; });

    /* ── Submit ── */
    function showError(msg) {
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }
    function clearError() { errorEl.hidden = true; errorEl.textContent = ''; }

    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearError();

      var rating = parseInt(rateValue.value, 10) || 0;
      var nameEl = form.querySelector('input[name="name"]');
      var emailEl = form.querySelector('input[name="email"]');
      var phoneEl = form.querySelector('input[name="phone"]');
      var consentEl = form.querySelector('input[name="consent"]');
      var smsEl = form.querySelector('input[name="sms_consent"]');

      if (!rating) { showError('Please choose a star rating.'); stars[0].focus(); return; }
      if (bodyInput.value.trim().length < MIN_BODY) { showError('Please write at least ' + MIN_BODY + ' characters.'); bodyInput.focus(); return; }
      if (!nameEl.value.trim()) { showError('Please enter your name.'); nameEl.focus(); return; }
      if (!EMAIL_RE.test(emailEl.value.trim())) { showError('Please enter a valid email.'); emailEl.focus(); return; }
      if (consentEl && !consentEl.checked) { showError('Please agree to the review guidelines to continue.'); return; }
      if (smsEl && smsEl.checked && !(phoneEl && phoneEl.value.trim())) {
        showError('Add your phone number to get SMS offers, or untick the text-updates box.');
        if (phoneEl) phoneEl.focus();
        return;
      }

      submitBtn.disabled = true;
      var prevLabel = submitLabel.textContent;
      submitLabel.textContent = 'Submitting…';

      fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating: rating,
          body: bodyInput.value.trim(),
          title: titleInput.value.trim(),
          name: nameEl.value.trim(),
          email: emailEl.value.trim(),
          phone: phoneEl ? phoneEl.value.trim() : '',
          product_id: form.dataset.productId,
          sms_consent: !!(smsEl && smsEl.checked),
          consent_text: (smsEl && smsEl.checked) ? (smsEl.dataset.consentText || '') : ''
        })
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (r) {
          if (!r.ok) throw new Error((r.data && r.data.error) || 'Could not submit review. Please try again.');
          // Success — swap body/footer for the thank-you state
          if (body) body.hidden = true;
          if (foot) foot.hidden = true;
          if (done) done.hidden = false;
        })
        .catch(function (err) {
          showError(err.message || 'Something went wrong. Please try again.');
          submitBtn.disabled = false;
          submitLabel.textContent = prevLabel;
        });
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
})();
